import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { errMsg } from "@/lib/api";
import { PageHeader, Loading } from "@/components/common";
import { brl, fmtDateTime } from "@/lib/format";
import { Loader2, RefreshCw, Trash2, AlertTriangle, CheckCircle2, Search, Building2, Download } from "@/components/icons";

// CORRECAO_EMERGENT_FINAL_V2 Item 3 — Ferramentas do Administrador.
// Só visível para role admin. Duas ações que exibem o preview antes e só
// executam depois de "Confirmar". Cada execução fica auditada.
export default function FerramentasAdmin() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [previewProb, setPreviewProb] = useState(null);
  const [previewTeste, setPreviewTeste] = useState(null);
  const [busy, setBusy] = useState("");
  const [audit, setAudit] = useState([]);

  // Correção pontual de CNPJ.
  const [cnpjBusca, setCnpjBusca] = useState("");
  const [cnpjResultados, setCnpjResultados] = useState([]);
  const [cnpjSelecionado, setCnpjSelecionado] = useState(null); // cliente escolhido
  const [cnpjNovo, setCnpjNovo] = useState("");
  const [cnpjPreview, setCnpjPreview] = useState(null);

  // Varredura CNPJ divergente.
  const [varredura, setVarredura] = useState(null);
  const [varreduraLimit, setVarreduraLimit] = useState(100);

  const carregarAudit = () => {
    api.get("/admin/ferramentas/audit").then((r) => setAudit(r.data?.items || [])).catch(() => {});
  };
  useEffect(() => { if (isAdmin) carregarAudit(); }, [isAdmin]);

  const carregarPrevProb = async () => {
    setBusy("prob-preview");
    try {
      const { data } = await api.get("/admin/ferramentas/recalcular-prob");
      setPreviewProb(data);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(""); }
  };
  const confirmarProb = async () => {
    setBusy("prob-run");
    try {
      const { data } = await api.post("/admin/ferramentas/recalcular-prob");
      toast.success(`${data.ajustadas === 1 ? "1 oportunidade ajustada" : `${data.ajustadas} oportunidades ajustadas`}`);
      setPreviewProb(null); carregarAudit();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(""); }
  };

  const carregarPrevTeste = async () => {
    setBusy("teste-preview");
    try {
      const { data } = await api.get("/admin/ferramentas/excluir-teste");
      setPreviewTeste(data);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(""); }
  };
  const confirmarTeste = async () => {
    setBusy("teste-run");
    try {
      const { data } = await api.post("/admin/ferramentas/excluir-teste");
      toast.success(`${data.apagados === 1 ? "1 registro apagado" : `${data.apagados} registros apagados`}`);
      setPreviewTeste(null); carregarAudit();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(""); }
  };

  // ===== Correção de CNPJ =====
  const buscarClientes = async () => {
    const q = cnpjBusca.trim();
    if (q.length < 3) { toast.error("Digite ao menos 3 caracteres (nome ou CNPJ)."); return; }
    setBusy("cnpj-busca");
    try {
      const { data } = await api.get("/admin/ferramentas/clientes/buscar", { params: { q } });
      setCnpjResultados(data?.items || []);
      if (!data?.items?.length) toast.info("Nenhum cliente encontrado.");
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(""); }
  };
  const abrirClienteParaCorrecao = (c) => {
    setCnpjSelecionado(c);
    setCnpjNovo("");
    setCnpjPreview(null);
  };
  const previewCnpj = async () => {
    if (!cnpjSelecionado || !cnpjNovo.trim()) return;
    setBusy("cnpj-preview");
    try {
      const { data } = await api.get(`/admin/ferramentas/clientes/${cnpjSelecionado.id}/corrigir-cnpj`, {
        params: { cnpj: cnpjNovo },
      });
      setCnpjPreview(data);
      if (!data.cnpj_valido) toast.error("CNPJ inválido (dígito verificador não bate).");
      else if (data.colisao) toast.error("CNPJ já em uso por outro cliente.");
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(""); }
  };
  const confirmarCnpj = async () => {
    if (!cnpjSelecionado) return;
    setBusy("cnpj-run");
    try {
      const { data } = await api.post(`/admin/ferramentas/clientes/${cnpjSelecionado.id}/corrigir-cnpj`, { cnpj: cnpjNovo });
      toast.success(`CNPJ atualizado: ${data.cnpj_anterior || "(vazio)"} → ${data.cnpj_novo}`);
      setCnpjSelecionado(null); setCnpjNovo(""); setCnpjPreview(null); setCnpjResultados([]); setCnpjBusca("");
      carregarAudit();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(""); }
  };

  // ===== Varredura CNPJ divergente =====
  const rodarVarredura = async () => {
    setBusy("varredura");
    setVarredura(null);
    try {
      const { data } = await api.get("/admin/saude-cadastro/cnpj-divergentes", { params: { limit: varreduraLimit } });
      setVarredura(data);
      toast.success(`${data.divergentes_total} divergente(s) em ${data.verificados} verificado(s).`);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(""); }
  };
  const baixarVarreduraCSV = () => {
    if (!varredura?.divergentes?.length) return;
    const header = ["id", "nome", "razao_social_crm", "cnpj", "razao_social_api", "owner_name"];
    const linhas = varredura.divergentes.map((d) => [
      d.id,
      d.nome || "",
      d.razao_social || "",
      d.cnpj || "",
      d.razao_social_api || "",
      d.owner_name || "",
    ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","));
    const csv = [header.join(","), ...linhas].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `cnpj-divergentes-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!user) return <Loading />;
  if (!isAdmin) return (
    <div className="max-w-xl">
      <PageHeader title="Ferramentas do administrador" />
      <div className="rounded-2xl border border-warn/30 bg-warn-soft/40 p-6 flex items-center gap-3">
        <AlertTriangle className="w-5 h-5 text-warn" />
        <span className="text-sm text-navy">Acesso restrito ao perfil Administrador.</span>
      </div>
    </div>
  );

  return (
    <div className="max-w-3xl">
      <PageHeader title="Ferramentas do administrador" subtitle="Executam só depois de confirmar. Toda ação fica registrada." />

      <section className="bg-card rounded-2xl border border-black/5 p-5 shadow-[0_4px_20px_rgb(0,0,0,0.03)] mb-4">
        <div className="flex items-center gap-2 mb-2"><RefreshCw className="w-4 h-4 text-brand" />
          <h3 className="text-sm font-semibold text-navy">Recalcular probabilidades</h3></div>
        <p className="text-xs text-muted-foreground mb-3">
          Aplica às oportunidades abertas: Prospecção 10% · Qualificação 20% · Testes 40% · Proposta 60% · Negociação 80%.
          Ganho e Perdido não são alterados.
        </p>
        {!previewProb ? (
          <button onClick={carregarPrevProb} disabled={busy !== ""} data-testid="ferr-prob-preview"
            className="h-10 px-4 rounded-lg border border-input text-sm font-semibold hover:bg-slate-50 flex items-center gap-2 disabled:opacity-60">
            {busy === "prob-preview" && <Loader2 className="w-4 h-4 animate-spin" />} Ver o que será ajustado
          </button>
        ) : (
          <div className="rounded-xl bg-slate-50 border border-black/5 p-3">
            <div className="text-sm text-navy mb-2">
              <b>{previewProb.total}</b> {previewProb.total === 1 ? "oportunidade será ajustada" : "oportunidades serão ajustadas"}.
              {previewProb.ja_ok > 0 && <span className="text-muted-foreground"> {previewProb.ja_ok} já estão OK.</span>}
            </div>
            <ul className="text-xs text-slate-600 mb-3">
              {(previewProb.por_etapa || []).map((x) => (
                <li key={x.stage}>{x.stage}: {x.count} para {x.prob_alvo}%</li>
              ))}
            </ul>
            <div className="flex gap-2">
              <button onClick={() => setPreviewProb(null)} className="h-10 px-4 rounded-lg border border-input text-sm">Cancelar</button>
              <button onClick={confirmarProb} disabled={busy !== ""} data-testid="ferr-prob-confirm"
                className="h-10 px-5 rounded-lg bg-navy text-white text-sm font-semibold disabled:opacity-60 flex items-center gap-2">
                {busy === "prob-run" && <Loader2 className="w-4 h-4 animate-spin" />} Confirmar
              </button>
            </div>
          </div>
        )}
      </section>

      <section className="bg-card rounded-2xl border border-black/5 p-5 shadow-[0_4px_20px_rgb(0,0,0,0.03)] mb-4">
        <div className="flex items-center gap-2 mb-2"><Trash2 className="w-4 h-4 text-bad" />
          <h3 className="text-sm font-semibold text-navy">Excluir registros de teste</h3></div>
        <p className="text-xs text-muted-foreground mb-3">
          Apaga o cliente &quot;TESTE AUDITORIA FOTO - APAGAR&quot; (com deal, atividade e foto), as oportunidades perdidas
          &quot;Prospecção: Gráfica Formato&quot; (motivo &quot;somente um teste de localização&quot;) e &quot;Químicos de higienização&quot;
          (motivo &quot;era só um teste&quot;), e qualquer registro cujo nome/título contenha &quot;TESTE REGRESSAO&quot;.
        </p>
        {!previewTeste ? (
          <button onClick={carregarPrevTeste} disabled={busy !== ""} data-testid="ferr-teste-preview"
            className="h-10 px-4 rounded-lg border border-input text-sm font-semibold hover:bg-slate-50 flex items-center gap-2 disabled:opacity-60">
            {busy === "teste-preview" && <Loader2 className="w-4 h-4 animate-spin" />} Ver o que será apagado
          </button>
        ) : (
          <div className="rounded-xl bg-slate-50 border border-black/5 p-3">
            <div className="text-sm text-navy mb-2">
              Total a apagar: <b>{previewTeste.total}</b> {previewTeste.total === 1 ? "registro" : "registros"}.
            </div>
            <ul className="text-xs text-slate-600 mb-3 space-y-0.5 max-h-64 overflow-y-auto">
              {(previewTeste.itens || []).map((x, i) => (
                <li key={i}>· [{x.tipo}] {x.descricao}</li>
              ))}
            </ul>
            <div className="flex gap-2">
              <button onClick={() => setPreviewTeste(null)} className="h-10 px-4 rounded-lg border border-input text-sm">Cancelar</button>
              <button onClick={confirmarTeste} disabled={busy !== ""} data-testid="ferr-teste-confirm"
                className="h-10 px-5 rounded-lg bg-bad text-white text-sm font-semibold disabled:opacity-60 flex items-center gap-2">
                {busy === "teste-run" && <Loader2 className="w-4 h-4 animate-spin" />} Confirmar exclusão
              </button>
            </div>
          </div>
        )}
      </section>

      <section className="bg-card rounded-2xl border border-black/5 p-5 shadow-[0_4px_20px_rgb(0,0,0,0.03)] mb-4">
        <div className="flex items-center gap-2 mb-2"><Building2 className="w-4 h-4 text-brand" />
          <h3 className="text-sm font-semibold text-navy">Corrigir CNPJ de cliente</h3></div>
        <p className="text-xs text-muted-foreground mb-3">
          Caso o cliente tenha sido salvo com o CNPJ de outra empresa, use isto para substituir pelo
          CNPJ correto. Só altera `cnpj` e `cnpj_digits`; o resto do cadastro é preservado.
          A alteração fica registrada no histórico abaixo.
        </p>
        <div className="flex gap-2 mb-3">
          <input
            value={cnpjBusca}
            onChange={(e) => setCnpjBusca(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") buscarClientes(); }}
            placeholder="Buscar por nome ou CNPJ (mín. 3 caracteres)"
            data-testid="corrigir-cnpj-busca"
            className="flex-1 h-10 px-3 rounded-lg border border-input text-sm"
          />
          <button onClick={buscarClientes} disabled={busy !== ""} data-testid="corrigir-cnpj-buscar-btn"
            className="h-10 px-4 rounded-lg border border-input text-sm font-semibold hover:bg-slate-50 flex items-center gap-2 disabled:opacity-60">
            {busy === "cnpj-busca" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            Buscar
          </button>
        </div>
        {cnpjResultados.length > 0 && !cnpjSelecionado && (
          <ul className="rounded-xl bg-slate-50 border border-black/5 divide-y divide-black/5 max-h-72 overflow-y-auto mb-3">
            {cnpjResultados.map((c) => (
              <li key={c.id} className="p-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-navy truncate">{c.nome || c.razao_social || "(sem nome)"}</div>
                  <div className="text-xs text-slate-600 truncate">
                    CNPJ atual: <b>{c.cnpj || "(vazio)"}</b> · dono: {c.owner_name || "—"} · id: <code className="text-[10px]">{c.id}</code>
                  </div>
                </div>
                <button onClick={() => abrirClienteParaCorrecao(c)} data-testid={`corrigir-cnpj-escolher-${c.id}`}
                  className="h-9 px-3 rounded-lg bg-navy text-white text-xs font-semibold shrink-0">
                  Selecionar
                </button>
              </li>
            ))}
          </ul>
        )}
        {cnpjSelecionado && (
          <div className="rounded-xl bg-slate-50 border border-black/5 p-3">
            <div className="text-sm mb-2">
              <div className="font-semibold text-navy">{cnpjSelecionado.nome || cnpjSelecionado.razao_social}</div>
              <div className="text-xs text-slate-600 mt-0.5">
                CNPJ atual: <b>{cnpjSelecionado.cnpj || "(vazio)"}</b> · dono: {cnpjSelecionado.owner_name || "—"}
              </div>
            </div>
            <div className="flex gap-2 mb-2">
              <input
                value={cnpjNovo}
                onChange={(e) => { setCnpjNovo(e.target.value); setCnpjPreview(null); }}
                placeholder="CNPJ novo (ex: 06.140.935/0001-50)"
                data-testid="corrigir-cnpj-novo"
                className="flex-1 h-10 px-3 rounded-lg border border-input text-sm"
              />
              <button onClick={previewCnpj} disabled={busy !== "" || !cnpjNovo.trim()} data-testid="corrigir-cnpj-preview-btn"
                className="h-10 px-4 rounded-lg border border-input text-sm font-semibold hover:bg-slate-50 flex items-center gap-2 disabled:opacity-60">
                {busy === "cnpj-preview" && <Loader2 className="w-4 h-4 animate-spin" />} Verificar
              </button>
            </div>
            {cnpjPreview && (
              <div className="text-xs mb-2">
                <div>CNPJ válido: <b className={cnpjPreview.cnpj_valido ? "text-ok" : "text-bad"}>{cnpjPreview.cnpj_valido ? "sim" : "não"}</b></div>
                {cnpjPreview.colisao ? (
                  <div className="text-bad mt-1">
                    ⚠ Já em uso por outro cliente: <b>{cnpjPreview.colisao.nome}</b> (dono: {cnpjPreview.colisao.owner_name || "—"}).
                  </div>
                ) : cnpjPreview.cnpj_valido ? (
                  <div className="text-ok mt-1">✓ Nenhuma colisão. Pode confirmar.</div>
                ) : null}
              </div>
            )}
            <div className="flex gap-2">
              <button onClick={() => { setCnpjSelecionado(null); setCnpjNovo(""); setCnpjPreview(null); }}
                className="h-10 px-4 rounded-lg border border-input text-sm">Cancelar</button>
              <button onClick={confirmarCnpj} disabled={busy !== "" || !cnpjPreview?.cnpj_valido || !!cnpjPreview?.colisao}
                data-testid="corrigir-cnpj-confirmar"
                className="h-10 px-5 rounded-lg bg-navy text-white text-sm font-semibold disabled:opacity-60 flex items-center gap-2">
                {busy === "cnpj-run" && <Loader2 className="w-4 h-4 animate-spin" />} Confirmar correção
              </button>
            </div>
          </div>
        )}
      </section>

      <section className="bg-card rounded-2xl border border-black/5 p-5 shadow-[0_4px_20px_rgb(0,0,0,0.03)] mb-4">
        <div className="flex items-center gap-2 mb-2"><Search className="w-4 h-4 text-brand" />
          <h3 className="text-sm font-semibold text-navy">Varredura: CNPJ divergente da razão social</h3></div>
        <p className="text-xs text-muted-foreground mb-3">
          Consulta a BrasilAPI para os clientes com CNPJ cadastrado e lista aqueles cuja razão social
          registrada no CRM não bate com a razão social da Receita. Somente leitura — não altera
          nenhum registro. Pode levar alguns segundos.
        </p>
        <div className="flex gap-2 mb-3">
          <label className="text-xs text-slate-600 flex items-center gap-2">
            Limite:
            <select value={varreduraLimit} onChange={(e) => setVarreduraLimit(Number(e.target.value))}
              className="h-9 px-2 rounded-lg border border-input text-sm">
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={500}>500</option>
            </select>
          </label>
          <button onClick={rodarVarredura} disabled={busy !== ""} data-testid="varredura-cnpj-btn"
            className="h-10 px-4 rounded-lg border border-input text-sm font-semibold hover:bg-slate-50 flex items-center gap-2 disabled:opacity-60">
            {busy === "varredura" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            Rodar varredura
          </button>
          {varredura?.divergentes_total > 0 && (
            <button onClick={baixarVarreduraCSV} data-testid="varredura-csv-btn"
              className="h-10 px-4 rounded-lg border border-input text-sm font-semibold hover:bg-slate-50 flex items-center gap-2">
              <Download className="w-4 h-4" /> CSV
            </button>
          )}
        </div>
        {varredura && (
          <div className="rounded-xl bg-slate-50 border border-black/5 p-3">
            <div className="text-sm text-navy mb-2">
              Verificados: <b>{varredura.verificados}</b> ·
              Divergentes: <b className={varredura.divergentes_total > 0 ? "text-bad" : "text-ok"}>{varredura.divergentes_total}</b>
              {varredura.erros_consulta > 0 && (
                <span className="text-slate-500"> · {varredura.erros_consulta} erro(s) de consulta</span>
              )}
            </div>
            {varredura.divergentes_total === 0 ? (
              <div className="text-xs text-ok">Nenhuma divergência encontrada neste lote.</div>
            ) : (
              <ul className="text-xs space-y-1 max-h-80 overflow-y-auto">
                {varredura.divergentes.map((d) => (
                  <li key={d.id} className="rounded-lg bg-white border border-black/5 p-2">
                    <div className="font-semibold text-navy">{d.nome || d.razao_social}</div>
                    <div className="text-slate-600">
                      CRM: <code>{d.razao_social || d.nome}</code> · CNPJ: <b>{d.cnpj}</b>
                    </div>
                    <div className="text-slate-600">
                      Receita: <code>{d.razao_social_api}</code>
                      {d.nome_fantasia_api ? <> · fantasia: <code>{d.nome_fantasia_api}</code></> : null}
                    </div>
                    <div className="text-slate-500">Dono: {d.owner_name || "—"} · id: <code className="text-[10px]">{d.id}</code></div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </section>

      <section className="bg-card rounded-2xl border border-black/5 p-5 shadow-[0_4px_20px_rgb(0,0,0,0.03)]">
        <div className="flex items-center gap-2 mb-2"><CheckCircle2 className="w-4 h-4 text-ok" />
          <h3 className="text-sm font-semibold text-navy">Histórico de execuções</h3></div>
        {audit.length === 0 ? (
          <p className="text-xs text-muted-foreground">Nenhuma execução registrada.</p>
        ) : (
          <ul className="text-xs text-slate-600 space-y-1 max-h-64 overflow-y-auto">
            {audit.map((a) => (
              <li key={a.id}>
                {fmtDateTime(a.at)} · {a.action} · {a.user_name} · {(a.count || 0) === 1 ? "1 registro" : `${a.count || 0} registros`}
                {a.details ? <span className="text-slate-400"> — {a.details}</span> : null}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
